import { cn } from "@/lib/cn";
import { fmt } from "@/lib/format";
import { medalLabel } from "@/lib/theme";
import type { MedalItem } from "@/api/types";
import { Badge } from "./Badge";
import { MedalTooltip } from "./MedalTooltip";
import { NumberTicker } from "@/components/effects/NumberTicker";

export interface RankingRowProps {
  rank: number;
  username: string;
  team?: string | null;
  score: number | string;
  badges?: string[];
  medals?: MedalItem[];
  metricLabel: string;
  secondary?: { value: number | string; label?: string } | null;
}

/** Deterministic HSL hue from the team string so the same team always
 * gets the same aura colour. FNV-1a-ish rolling hash → hue 0..359. */
function teamHue(team: string | null | undefined): number {
  if (!team) return 260; // default violet
  let hash = 2166136261;
  for (let i = 0; i < team.length; i += 1) {
    hash ^= team.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  // Keep it positive and map to 0..359.
  return Math.abs(hash) % 360;
}

/** Single ranking row — rank pill + initial avatar + name + team + badges + score.
 *
 * Visual updates: animated violet→pink gradient pill for medal positions,
 * team-derived HSL aura on the avatar, hover translate + left accent bar,
 * and score animates up with NumberTicker. Accepts primitive props so every
 * tab can adapt its row shape to a common presentational layout. */
export function RankingRow({
  rank,
  username,
  team,
  score,
  badges = [],
  medals,
  metricLabel,
  secondary,
}: RankingRowProps) {
  const initial = (username ?? "?").charAt(0).toUpperCase();
  const rankIsMedal = rank > 0 && rank <= 3;
  const hue = teamHue(team);
  // Low saturation / mid lightness keeps the aura legible on the dark bg
  // without screaming over the content.
  const auraBg = `radial-gradient(circle at 30% 30%, hsla(${hue}, 55%, 62%, 0.55), hsla(${hue}, 45%, 40%, 0.15))`;
  const auraGlow = `hsla(${hue}, 70%, 60%, 0.35)`;

  return (
    <div
      className={cn(
        "group relative flex items-center gap-4 rounded-[14px] border border-border-1 bg-white/[0.02] px-4 py-3",
        "transition-all duration-200 ease-out hover:translate-x-1 hover:border-border-2 hover:bg-white/[0.04]",
      )}
    >
      {/* Left accent bar — invisible until hover, coloured by team hue
          so users can feel who's on their team as they scan the list. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 top-1/2 h-0 w-[3px] -translate-y-1/2 rounded-r-full opacity-0 transition-all duration-200 group-hover:h-8 group-hover:opacity-100"
        style={{ background: `hsl(${hue}, 70%, 60%)` }}
      />

      <div
        className={cn(
          "tabular flex h-8 w-10 items-center justify-center rounded-full text-xs font-bold",
          rankIsMedal ? "rank-pill-animated text-white" : "bg-white/5 text-text-3",
        )}
        aria-label={`Posición ${rank}`}
      >
        {medalLabel(rank)}
      </div>

      <div
        className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-white/90"
        style={{
          background: auraBg,
          boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 0 16px -4px ${auraGlow}`,
        }}
        aria-hidden
      >
        {initial}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <MedalTooltip medals={medals ?? []}>
            <div className="truncate font-semibold text-text-1">{username}</div>
          </MedalTooltip>
          {badges.length > 0 && (
            <div className="flex flex-shrink-0 flex-wrap gap-1">
              {badges.map((b) => (
                <Badge key={b} code={b} />
              ))}
            </div>
          )}
        </div>
        {team && <div className="truncate text-xs text-text-3">{team}</div>}
      </div>

      <div className="flex-shrink-0 text-right">
        <div className="tabular font-display text-lg font-bold text-text-1">
          {typeof score === "number" ? (
            <NumberTicker value={score} />
          ) : (
            score
          )}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-text-4">
          {metricLabel}
        </div>
      </div>

      {secondary && (
        <div className="flex-shrink-0 text-right">
          <div className="tabular text-sm font-semibold text-text-2">
            {typeof secondary.value === "number"
              ? fmt(secondary.value)
              : secondary.value}
          </div>
          {secondary.label && (
            <div className="text-[10px] uppercase tracking-wider text-text-4">
              {secondary.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
